/*
  "Save my bag" dialog (snippets/save-my-bag-dialog.liquid).

  Flow: shopper clicks a [data-save-bag-open] button (cart drawer or cart page) -> dialog
  opens, Cloudflare Turnstile loads on first use -> on submit we read the live cart from
  /cart.js and POST { email, bag_name, items, marketing_opt_in, root, turnstile_token } to the Worker,
  which emails the link. Nothing here is trusted by the Worker: it re-checks everything.

  The cart drawer traps keyboard focus inside itself, which would fight a modal dialog, so
  if the drawer is open we close it (with the theme's own "cart:close" event) before opening
  the dialog and reopen it afterwards ("cart:open").
*/
(function () {
  var dialog = document.getElementById('SaveBagDialog');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  var form = dialog.querySelector('[data-save-bag-form]');
  var emailInput = form.querySelector('input[name="email"]');
  var bagNameInput = form.querySelector('input[name="bag_name"]');
  var marketing = form.querySelector('[data-save-bag-marketing]');
  var turnstileBox = form.querySelector('[data-save-bag-turnstile]');
  var status = form.querySelector('[data-save-bag-status]');
  var submit = form.querySelector('[data-save-bag-submit]');
  var done = dialog.querySelector('[data-save-bag-done]');
  var partial = dialog.querySelector('[data-save-bag-partial]');
  var msg = dialog.dataset;

  var widgetId = null;
  var token = '';
  var busy = false;
  var turnstileLoading = null;
  var reopenDrawer = false;
  var opener = null;

  function setStatus(text, isError) {
    status.textContent = text || '';
    status.hidden = !text;
    status.classList.toggle('save-bag__status--error', !!isError);
  }

  function refreshSubmit() {
    submit.disabled = busy || !token;
  }

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve();
    if (turnstileLoading) return turnstileLoading;
    turnstileLoading = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = function () {
        turnstileLoading = null; // allow a retry next time the dialog opens
        reject(new Error('turnstile failed to load'));
      };
      document.head.appendChild(script);
    });
    return turnstileLoading;
  }

  function renderTurnstile() {
    return loadTurnstile().then(function () {
      if (widgetId !== null) {
        window.turnstile.reset(widgetId);
        return;
      }
      widgetId = window.turnstile.render(turnstileBox, {
        sitekey: msg.sitekey,
        action: 'save_bag',
        theme: 'light',
        callback: function (value) {
          token = value;
          refreshSubmit();
        },
        'expired-callback': function () {
          token = '';
          refreshSubmit();
        },
        'error-callback': function () {
          token = '';
          refreshSubmit();
          setStatus(msg.msgErrorVerify, true);
        }
      });
    });
  }

  // A Turnstile token can be used once, so every attempt needs a fresh one.
  function resetTurnstile() {
    token = '';
    refreshSubmit();
    if (window.turnstile && widgetId !== null) window.turnstile.reset(widgetId);
  }

  function openDialog(trigger) {
    opener = trigger;
    reopenDrawer = document.documentElement.classList.contains('js-drawer-open');
    if (reopenDrawer) document.dispatchEvent(new CustomEvent('cart:close'));

    form.hidden = false;
    done.hidden = true;
    partial.hidden = true;
    setStatus('');
    busy = false;
    submit.textContent = msg.msgSubmit;
    refreshSubmit();

    dialog.showModal();
    renderTurnstile().catch(function () {
      setStatus(msg.msgErrorVerify, true);
    });
  }

  function closeDialog() {
    if (dialog.open) dialog.close();
  }

  dialog.addEventListener('close', function () {
    if (reopenDrawer) {
      document.dispatchEvent(new CustomEvent('cart:open'));
    } else if (opener && document.contains(opener)) {
      opener.focus();
    }
    reopenDrawer = false;
  });

  // Click on the dimmed backdrop (the <dialog> itself, outside the panel) closes it.
  dialog.addEventListener('click', function (event) {
    if (event.target === dialog) closeDialog();
  });

  // The drawer is rebuilt by script, so listen on the document rather than on the buttons.
  document.addEventListener('click', function (event) {
    var openButton = event.target.closest('[data-save-bag-open]');
    if (openButton) {
      event.preventDefault();
      openDialog(openButton);
      return;
    }
    if (event.target.closest('[data-save-bag-close]')) {
      event.preventDefault();
      closeDialog();
    }
  });

  function errorFor(statusCode) {
    if (statusCode === 429) return msg.msgErrorRateLimited;
    if (statusCode === 422) return msg.msgErrorUnavailable;
    if (statusCode === 400) return msg.msgErrorInvalidEmail;
    if (statusCode === 403) return msg.msgErrorVerify;
    return msg.msgErrorGeneric;
  }

  function showSuccess(skipped) {
    form.hidden = true;
    done.hidden = false;
    if (skipped > 0) {
      partial.textContent = msg.msgSuccessPartial;
      partial.hidden = false;
    }
    done.focus();
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'save_bag_submit', { marketing_opt_in: marketing.checked });
    }
  }

  form.addEventListener('submit', function (event) {
    event.preventDefault();
    if (busy) return;

    if (!emailInput.checkValidity()) {
      setStatus(msg.msgErrorInvalidEmail, true);
      emailInput.focus();
      return;
    }
    if (!token) {
      setStatus(msg.msgErrorVerify, true);
      return;
    }

    busy = true;
    refreshSubmit();
    submit.textContent = msg.msgSending;
    setStatus('');

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var submittedToken = token;

    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('cart ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        var items = (cart.items || []).map(function (item) {
          return { variant_id: String(item.variant_id), quantity: item.quantity };
        });
        if (!items.length) {
          var empty = new Error('empty');
          empty.userMessage = msg.msgErrorEmpty;
          throw empty;
        }
        return fetch(msg.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: emailInput.value.trim(),
            bag_name: bagNameInput.value.trim(),
            items: items,
            marketing_opt_in: marketing.checked,
            root: root,
            turnstile_token: submittedToken
          })
        });
      })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (body) {
            if (response.ok && body.ok) {
              showSuccess(body.skipped || 0);
              return;
            }
            // Kept for diagnosis: the shopper sees a friendly message, the console gets the cause.
            console.warn('Save my bag: request failed', response.status, body.error || '');
            setStatus(errorFor(response.status), true);
          });
      })
      .catch(function (error) {
        if (!error.userMessage) console.warn('Save my bag: request error', error);
        setStatus(error.userMessage || msg.msgErrorGeneric, true);
      })
      .then(function () {
        busy = false;
        submit.textContent = msg.msgSubmit;
        resetTurnstile();
      });
  });
})();
